/* eslint-disable max-classes-per-file */
export class Specialty {
  id!: number;
  title!: string;
  tag!: string;
  externalTitle!: string;
  externalTag!: string;
  topSpecialty!: boolean;

  constructor(json: ISpecialtyApiResponse) {
    this.id = json.id;
    this.title = json.title;
    this.tag = json.tag;
    this.externalTitle = json.external_title;
    this.externalTag = json.external_tag;
    this.topSpecialty = json.top_specialty;
  }
}

export interface ISpecialtyApiResponse {
  id: number;
  title: string;
  tag: string;
  external_title: string;
  external_tag: string;
  top_specialty: boolean;
}

export class TreatmentPreference {
  id!: number;
  title!: string;
  tag!: string;
  externalTitle!: string;
  externalTag!: string;

  constructor(json: ITreatmentPreferenceApiResponse) {
    this.id = json.id;
    this.title = json.title;
    this.tag = json.tag;
    this.externalTitle = json.external_title;
    this.externalTag = json.external_tag;
  }
}

export interface ITreatmentPreferenceApiResponse {
  id: number;
  title: string;
  tag: string;
  external_title: string;
  external_tag: string;
}

export class Age {
  id!: number;
  title!: string;
  tag!: string;
  externalTitle!: string;
  externalTag!: string;

  constructor(json: IAgeApiResponse) {
    this.id = json.id;
    this.title = json.title;
    this.tag = json.tag;
    this.externalTitle = json.external_title;
    this.externalTag = json.external_tag;
  }
}

export interface IAgeApiResponse {
  id: number;
  title: string;
  tag: string;
  external_title: string;
  external_tag: string;
}

export class Modality {
  id!: number;
  title!: string;
  tag!: string;
  externalTitle!: string;
  externalTag!: string;

  constructor(json: IModalityApiResponse) {
    this.id = json.id;
    this.title = json.title;
    this.tag = json.tag;
    this.externalTitle = json.external_title;
    this.externalTag = json.external_tag;
  }
}

export interface IModalityApiResponse {
  id: number;
  title: string;
  tag: string;
  external_title: string;
  external_tag: string;
}
export interface ICoordinates {
  lat: number;
  lng: number;
}

export class Location {
  id!: number;
  name!: string;
  city!: string;
  state!: string;
  postalCode!: string;
  locationType!: LocationType;
  coordinates!: ICoordinates;

  constructor(json: ILocationApiResponse) {
    this.id = json.id;
    this.name = json.name;
    this.city = json.city;
    this.state = json.state;
    this.postalCode = json.postal_code;
    this.locationType = json.location_type;
    this.coordinates = json.coordinates;
  }
}

export enum LocationType {
  PHYSICAL = 'physical',
  VIRTUAL = 'virtual'
}

export interface ILocationApiResponse {
  id: number;
  name: string;
  city: string;
  state: string;
  postal_code: string;
  location_type: LocationType;
  coordinates: ICoordinates;
}

export class Licensure {
  id!: string;
  startDate!: string;
  endDate?: string | null;
  licensureId!: string;
  name!: string;
  abbreviation!: string;
  state!: string;

  constructor(json: ILicensureApiResponse) {
    this.id = json.id;
    this.startDate = json.start_date;
    this.endDate = json.end_date;
    this.licensureId = json.licensure_id;
    this.name = json.name;
    this.abbreviation = json.abbreviation;
    this.state = json.state;
  }
}

export interface ILicensureApiResponse {
  id: string;
  start_date: string;
  end_date?: string | null;
  licensure_id: string;
  name: string;
  abbreviation: string;
  state: string;
}

/* eslint-disable @typescript-eslint/naming-convention */
export interface IProviderApiResponse {
  provider_account_id: string;
  id: number;
  display_name: string;
  display_first_name: string;
  pronouns: string | null;
  bio: string | null;
  mini_bio: string | null;
  gender: string;
  is_accepting_clients: boolean;
  is_psychiatrist: boolean;
  session_types_offered: string[];
  has_direct_scheduling: boolean;
  has_propose_a_time: boolean;
  availability_score: number;
  specialties: ISpecialtyApiResponse[];
  treatment_preferences: ITreatmentPreferenceApiResponse[];
  ages: IAgeApiResponse[];
  modalities: IModalityApiResponse[];
  locations: ILocationApiResponse[];
  licensures: ILicensureApiResponse[];
  profile_photo_url: string;
  public_profile_url: string;
  saas_enabled: boolean;
  ebcg_embrace_provider?: boolean;
}
/* eslint-enable @typescript-eslint/naming-convention */

export interface IProvider {
  providerId: number;
  providerAccountId: string;
  displayName: string;
  displayFirstName: string;
  pronouns: string | null;
  bio: string | null;
  miniBio: string | null;
  gender: string;
  isAcceptingClients: boolean;
  isPsychiatrist: boolean;
  sessionTypesOffered: string[];
  hasDirectScheduling: boolean;
  hasProposeATime: boolean;
  availabilityScore: number;
  specialties: Specialty[];
  treatmentPreferences: TreatmentPreference[];
  ages: Age[];
  modalities: Modality[];
  locations: Location[];
  licensures: Licensure[];
  profilePhotoUrl: string;
  publicProfileUrl: string;
  shareCode: string;
  saasEnabled: boolean;
  ebcgEmbraceProvider?: boolean;
}

export class Provider implements IProvider {
  providerId!: number;
  providerAccountId!: string;
  displayName!: string;
  displayFirstName!: string;
  pronouns!: string | null;
  bio!: string | null;
  miniBio!: string | null;
  gender!: string;
  isAcceptingClients!: boolean;
  isPsychiatrist!: boolean;
  sessionTypesOffered!: string[];
  availabilityScore!: number;
  specialties!: Specialty[];
  treatmentPreferences!: TreatmentPreference[];
  ages!: Age[];
  modalities!: Modality[];
  locations!: Location[];
  licensures!: Licensure[];
  profilePhotoUrl!: string;
  publicProfileUrl!: string;
  hasDirectScheduling!: boolean;
  hasProposeATime!: boolean;
  shareCode!: string;
  saasEnabled!: boolean;
  ebcgEmbraceProvider?: boolean;

  constructor(json: IProviderApiResponse) {
    this.providerId = json.id;
    this.providerAccountId = json.provider_account_id;
    this.displayName = json.display_name;
    this.displayFirstName = json.display_first_name;
    this.pronouns = json.pronouns;
    this.bio = json.bio;
    this.miniBio = json.mini_bio;
    this.gender = json.gender;
    this.hasDirectScheduling = json.has_direct_scheduling;
    this.hasProposeATime = json.has_propose_a_time;
    this.isAcceptingClients = json.is_accepting_clients;
    this.isPsychiatrist = json.is_psychiatrist;
    this.sessionTypesOffered = json.session_types_offered;
    this.availabilityScore = json.availability_score;
    this.specialties = json.specialties.map((specialty) => new Specialty(specialty));
    this.treatmentPreferences = json.treatment_preferences.map((treatmentPreference) => new TreatmentPreference(treatmentPreference));
    this.ages = json.ages.map((age) => new Age(age));
    this.modalities = json.modalities.map((modality) => new Modality(modality));
    this.locations = json.locations.map((location) => new Location(location));
    this.licensures = json.licensures.map((licensure) => new Licensure(licensure));
    this.profilePhotoUrl = json.profile_photo_url;
    this.publicProfileUrl = json.public_profile_url;
    this.saasEnabled = json.saas_enabled;
    this.ebcgEmbraceProvider = json.ebcg_embrace_provider ?? false;

    // Parse the share code from the public profile url by grabbing "akmf8w29" from
    // "http://localhost:4205/akmf8w29/psychiatry_provider_4-tester-provider"
    if (this.publicProfileUrl) {
      const parts = this.publicProfileUrl.split('/');
      this.shareCode = parts.length >= 2 ? parts[parts.length - 2] : '';
    } else {
      this.shareCode = '';
    }
  }
}
